import React, { useState, useEffect, useCallback } from 'react';
import { Check, Crown, Zap, Star, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// PayPal Client ID from environment
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || 'sb'; // 'sb' for sandbox

const PricingModal = ({ isOpen, onClose, onSelectPlan, user }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('stripe'); // 'stripe' or 'paypal'
  const [showPayPal, setShowPayPal] = useState(false);
  const [currentPlanForPayPal, setCurrentPlanForPayPal] = useState(null);

  // Check for payment return from Stripe or PayPal
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentResult = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    const provider = urlParams.get('provider');
    
    if (paymentResult === 'success') {
      if (provider === 'paypal') {
        // PayPal return - payment already captured
        setPaymentStatus('success');
        setTimeout(() => {
          setPaymentStatus(null);
          onClose();
        }, 3000);
      } else if (sessionId) {
        // Stripe return - poll for status
        pollPaymentStatus(sessionId);
      }
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentResult === 'cancelled') {
      setError('התשלום בוטל');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Poll payment status
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setError('בדיקת התשלום נכשלה. אנא בדוק את המייל לאישור.');
      return;
    }

    try {
      setPaymentStatus('checking');
      const response = await axios.get(`${API}/stripe/checkout-status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        // Update local subscription
        const subscription = {
          type: response.data.plan_id,
          plan: response.data.plan_id === 'monthly' ? 'מנוי חודשי' : 'מנוי שנתי',
          startDate: new Date().toISOString()
        };
        localStorage.setItem('my_doctor_subscription', JSON.stringify(subscription));
        
        // Notify parent
        if (onSelectPlan) {
          onSelectPlan(subscription);
        }
        
        setTimeout(() => {
          setPaymentStatus(null);
          onClose();
        }, 3000);
      } else if (response.data.status === 'expired') {
        setError('התשלום פג תוקף. אנא נסה שוב.');
        setPaymentStatus(null);
      } else {
        // Still pending, continue polling
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  }, [onClose, onSelectPlan]);

  if (!isOpen) return null;

  const plans = [
    {
      id: 'trial',
      name: 'ניסיון חינם',
      price: 0,
      period: '21 יום',
      description: 'התחל ללמוד בחינם',
      features: [
        'גישה מלאה לכל הקורסים',
        'בנק שאלות מלא',
        'סימולטורים למבחנים',
        'סרטוני לימוד',
        '21 יום ללא התחייבות'
      ],
      buttonText: 'התחל ניסיון חינם',
      popular: false,
      color: 'teal',
      icon: Zap
    },
    {
      id: 'monthly',
      name: 'מנוי חודשי',
      price: 29,
      period: 'לחודש',
      description: 'גמישות מקסימלית',
      features: [
        'גישה מלאה לכל הקורסים',
        'בנק שאלות מלא',
        'סימולטורים למבחנים',
        'סרטוני לימוד',
        'עדכונים שוטפים',
        'ביטול בכל עת'
      ],
      buttonText: 'הרשמה חודשית',
      popular: true,
      color: 'purple',
      icon: Star
    },
    {
      id: 'yearly',
      name: 'מנוי שנתי',
      price: 300,
      period: 'לשנה',
      originalPrice: 348,
      savings: 48,
      description: 'חיסכון של 14%',
      features: [
        'גישה מלאה לכל הקורסים',
        'בנק שאלות מלא',
        'סימולטורים למבחנים',
        'סרטוני לימוד',
        'עדכונים שוטפים',
        'תמיכה מועדפת',
        'חיסכון של ₪48'
      ],
      buttonText: 'הרשמה שנתית',
      popular: false,
      color: 'amber',
      icon: Crown
    }
  ];

  const handleSelectPlan = async (plan, method = 'stripe') => {
    setSelectedPlan(plan.id);
    setError(null);
    
    // Get user info (from prop or localStorage)
    const userId = user?.user_id || user?.id || localStorage.getItem('user_id');
    const userEmail = user?.email || localStorage.getItem('user_email');
    const userName = user?.name || localStorage.getItem('user_name');
    
    // Trial plan - no payment needed
    if (plan.id === 'trial') {
      setLoading(true);
      try {
        const response = await axios.post(`${API}/stripe/create-checkout`, {
          plan_id: 'trial',
          origin_url: window.location.origin,
          user_id: userId,
          user_email: userEmail,
          user_name: userName
        });
        
        if (response.data.success && response.data.is_trial) {
          // Save subscription locally
          const subscription = {
            type: 'trial',
            plan: plan.name,
            startDate: new Date().toISOString(),
            expiresAt: response.data.trial_ends_at,
            daysLeft: 21
          };
          localStorage.setItem('my_doctor_subscription', JSON.stringify(subscription));
          
          if (onSelectPlan) {
            onSelectPlan(subscription);
          }
          onClose();
        }
      } catch (err) {
        setError('שגיאה בהפעלת תקופת הניסיון');
        console.error('Trial activation error:', err);
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // PayPal payment
    if (method === 'paypal') {
      setCurrentPlanForPayPal(plan);
      setShowPayPal(true);
      return;
    }
    
    // Stripe payment - redirect to Stripe Checkout
    setLoading(true);
    try {
      const response = await axios.post(`${API}/stripe/create-checkout`, {
        plan_id: plan.id,
        origin_url: window.location.origin,
        user_id: userId,
        user_email: userEmail,
        user_name: userName
      });
      
      if (response.data.success && response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        setError('שגיאה ביצירת התשלום');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.detail || 'שגיאה ביצירת התשלום');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl max-w-5xl w-full p-8 relative" onClick={e => e.stopPropagation()}>
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 left-4 p-2 hover:bg-slate-100 rounded-full transition-colors"
          disabled={loading}
        >
          <X className="w-6 h-6 text-slate-500" />
        </button>

        {/* Payment Status Overlay */}
        {paymentStatus === 'checking' && (
          <div className="absolute inset-0 bg-white/90 rounded-3xl flex flex-col items-center justify-center z-10">
            <Loader2 className="w-16 h-16 text-purple-500 animate-spin mb-4" />
            <p className="text-xl font-bold text-slate-900">מאמת את התשלום...</p>
            <p className="text-slate-500">אנא המתן</p>
          </div>
        )}

        {paymentStatus === 'success' && (
          <div className="absolute inset-0 bg-white/90 rounded-3xl flex flex-col items-center justify-center z-10">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Check className="w-12 h-12 text-green-500" />
            </div>
            <p className="text-2xl font-black text-slate-900 mb-2">התשלום הושלם בהצלחה!</p>
            <p className="text-slate-500">ברוך הבא להצ'ייסר Premium</p>
          </div>
        )}

        {/* Loading Overlay */}
        {loading && !paymentStatus && (
          <div className="absolute inset-0 bg-white/90 rounded-3xl flex flex-col items-center justify-center z-10">
            <Loader2 className="w-16 h-16 text-teal-500 animate-spin mb-4" />
            <p className="text-xl font-bold text-slate-900">מעבד...</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="absolute top-16 left-4 right-4 bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-center z-20">
            {error}
            <button onClick={() => setError(null)} className="mr-2 font-bold">✕</button>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl font-black text-slate-900 mb-3">בחר את המסלול שלך</h2>
          <p className="text-slate-600">התחל ללמוד עוד היום עם גישה מלאה לכל התכנים</p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => {
            const Icon = plan.icon;
            const isSelected = selectedPlan === plan.id;
            
            return (
              <div 
                key={plan.id}
                className={`
                  relative rounded-2xl p-6 border-2 transition-all cursor-pointer
                  ${plan.popular 
                    ? 'border-purple-500 bg-purple-50 scale-105 shadow-xl' 
                    : isSelected 
                      ? 'border-teal-500 bg-teal-50' 
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  }
                `}
                onClick={() => handleSelectPlan(plan)}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute -top-3 right-4 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    הכי פופולרי
                  </div>
                )}

                {/* Savings Badge */}
                {plan.savings && (
                  <div className="absolute -top-3 left-4 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    חיסכון ₪{plan.savings}
                  </div>
                )}

                {/* Icon */}
                <div className={`
                  w-14 h-14 rounded-2xl flex items-center justify-center mb-4
                  ${plan.color === 'teal' ? 'bg-teal-100 text-teal-600' : ''}
                  ${plan.color === 'purple' ? 'bg-purple-100 text-purple-600' : ''}
                  ${plan.color === 'amber' ? 'bg-amber-100 text-amber-600' : ''}
                `}>
                  <Icon className="w-7 h-7" />
                </div>

                {/* Plan Name */}
                <h3 className="text-xl font-bold text-slate-900 mb-1">{plan.name}</h3>
                <p className="text-slate-500 text-sm mb-4">{plan.description}</p>

                {/* Price */}
                <div className="mb-6">
                  {plan.originalPrice && (
                    <span className="text-slate-400 line-through text-lg">₪{plan.originalPrice}</span>
                  )}
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-black text-slate-900">
                      {plan.price === 0 ? 'חינם' : `₪${plan.price}`}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-slate-500 text-sm">{plan.period}</span>
                    )}
                  </div>
                  {plan.price === 0 && (
                    <span className="text-slate-500 text-sm">{plan.period}</span>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                      <Check className={`w-5 h-5 flex-shrink-0 ${
                        plan.color === 'teal' ? 'text-teal-500' :
                        plan.color === 'purple' ? 'text-purple-500' :
                        'text-amber-500'
                      }`} />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Buttons */}
                {plan.price === 0 ? (
                  // Trial - single button
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectPlan(plan);
                    }}
                    disabled={loading}
                    className={`
                      w-full py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2
                      ${loading ? 'opacity-50 cursor-not-allowed' : ''}
                      bg-teal-600 text-white hover:bg-teal-700
                    `}
                    data-testid={`select-plan-${plan.id}`}
                  >
                    {loading && selectedPlan === plan.id ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        מעבד...
                      </>
                    ) : (
                      plan.buttonText
                    )}
                  </button>
                ) : (
                  // Paid plans - show payment method options
                  <div className="space-y-2">
                    {/* Apple Pay Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectPlan(plan, 'stripe');
                      }}
                      disabled={loading}
                      className="w-full py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 bg-black text-white hover:bg-gray-800"
                      data-testid={`select-plan-${plan.id}-apple`}
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                      </svg>
                       Pay
                    </button>
                    
                    {/* Stripe Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectPlan(plan, 'stripe');
                      }}
                      disabled={loading}
                      className={`
                        w-full py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2
                        ${loading ? 'opacity-50 cursor-not-allowed' : ''}
                        ${plan.popular
                          ? 'bg-purple-600 text-white hover:bg-purple-700'
                          : 'bg-amber-500 text-white hover:bg-amber-600'
                        }
                      `}
                      data-testid={`select-plan-${plan.id}-stripe`}
                    >
                      {loading && selectedPlan === plan.id ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          מעבד...
                        </>
                      ) : (
                        <>
                          💳 {plan.buttonText}
                        </>
                      )}
                    </button>
                    
                    {/* PayPal Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectPlan(plan, 'paypal');
                      }}
                      disabled={loading}
                      className="w-full py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 bg-[#0070ba] text-white hover:bg-[#005ea6]"
                      data-testid={`select-plan-${plan.id}-paypal`}
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.65h6.916c2.332 0 4.195.639 5.371 1.847.478.491.824 1.053 1.028 1.67.205.618.268 1.303.193 2.04v.001a6.84 6.84 0 0 1-.08.557c-.32 1.643-1.022 2.867-2.09 3.638-1.085.783-2.525 1.18-4.282 1.18h-1.08a.946.946 0 0 0-.934.795l-.79 5.024-.247 1.567a.531.531 0 0 1-.525.448z"/>
                      </svg>
                      PayPal
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* PayPal Modal */}
        {showPayPal && currentPlanForPayPal && (
          <div className="fixed inset-0 bg-black/50 z-60 flex items-center justify-center p-4" onClick={() => setShowPayPal(false)}>
            <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-slate-900 mb-4 text-center">תשלום עם PayPal</h3>
              <p className="text-slate-600 mb-4 text-center">
                {currentPlanForPayPal.name} - ₪{currentPlanForPayPal.price}
              </p>
              <PayPalScriptProvider options={{ 
                clientId: PAYPAL_CLIENT_ID, 
                currency: "ILS",
                intent: "capture"
              }}>
                <PayPalButtons
                  style={{ layout: "vertical", shape: "rect" }}
                  createOrder={async () => {
                    const userId = user?.user_id || user?.id || localStorage.getItem('user_id');
                    const userEmail = user?.email || localStorage.getItem('user_email');
                    const userName = user?.name || localStorage.getItem('user_name');
                    
                    const response = await axios.post(`${API}/paypal/create-order`, {
                      plan_id: currentPlanForPayPal.id,
                      origin_url: window.location.origin,
                      user_id: userId,
                      user_email: userEmail,
                      user_name: userName
                    });
                    return response.data.order_id;
                  }}
                  onApprove={async (data) => {
                    try {
                      const response = await axios.post(`${API}/paypal/capture-order`, {
                        order_id: data.orderID
                      });
                      if (response.data.success) {
                        const subscription = {
                          type: response.data.plan_id,
                          plan: currentPlanForPayPal.name,
                          startDate: new Date().toISOString(),
                          expiresAt: response.data.expires_at,
                          paymentMethod: 'paypal'
                        };
                        localStorage.setItem('my_doctor_subscription', JSON.stringify(subscription));
                        if (onSelectPlan) onSelectPlan(subscription);
                        setShowPayPal(false);
                        setPaymentStatus('success');
                        setTimeout(() => {
                          setPaymentStatus(null);
                          onClose();
                        }, 3000);
                      }
                    } catch (err) {
                      setError('שגיאה בעיבוד התשלום');
                      setShowPayPal(false);
                    }
                  }}
                  onError={(err) => {
                    console.error('PayPal error:', err);
                    setError('שגיאה בתשלום PayPal');
                    setShowPayPal(false);
                  }}
                  onCancel={() => {
                    setShowPayPal(false);
                  }}
                />
              </PayPalScriptProvider>
              <button 
                onClick={() => setShowPayPal(false)}
                className="w-full mt-4 py-2 text-slate-500 hover:text-slate-700"
              >
                ביטול
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8 text-slate-500 text-sm">
          <p>ביטול בכל עת • ללא התחייבות • תשלום מאובטח</p>
          <div className="flex items-center justify-center gap-4 mt-3">
            <span className="text-xs text-slate-400">🔒 SSL מוצפן</span>
            <span className="text-xs text-slate-400">💳 Stripe</span>
            <span className="text-xs text-slate-400">
              <svg className="w-4 h-4 inline" viewBox="0 0 24 24" fill="#003087">
                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.65h6.916c2.332 0 4.195.639 5.371 1.847.478.491.824 1.053 1.028 1.67.205.618.268 1.303.193 2.04v.001a6.84 6.84 0 0 1-.08.557c-.32 1.643-1.022 2.867-2.09 3.638-1.085.783-2.525 1.18-4.282 1.18h-1.08a.946.946 0 0 0-.934.795l-.79 5.024-.247 1.567a.531.531 0 0 1-.525.448z"/>
              </svg>
              PayPal
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Subscription Banner Component
const SubscriptionBanner = ({ daysLeft, onUpgrade }) => {
  if (daysLeft === null) return null;

  const isExpiringSoon = daysLeft <= 5;
  const isExpired = daysLeft <= 0;

  return (
    <div className={`
      fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96
      rounded-2xl p-4 shadow-xl z-40 flex items-center justify-between
      ${isExpired 
        ? 'bg-red-500 text-white' 
        : isExpiringSoon 
          ? 'bg-amber-500 text-white'
          : 'bg-teal-500 text-white'
      }
    `}>
      <div>
        <p className="font-bold">
          {isExpired 
            ? 'תקופת הניסיון הסתיימה'
            : `נותרו ${daysLeft} ימים לתקופת הניסיון`
          }
        </p>
        <p className="text-sm opacity-90">
          {isExpired 
            ? 'שדרג עכשיו להמשך גישה'
            : 'שדרג למנוי מלא'
          }
        </p>
      </div>
      <button
        onClick={onUpgrade}
        className={`
          px-4 py-2 rounded-xl font-bold transition-all
          ${isExpired 
            ? 'bg-white text-red-500 hover:bg-red-50'
            : 'bg-white/20 hover:bg-white/30'
          }
        `}
      >
        שדרג עכשיו
      </button>
    </div>
  );
};

export { PricingModal, SubscriptionBanner };
